// <Typewriter> — types plain text char-by-char with a blinking caret.
//
// Plain-text only (no innerHTML — text rendered via textContent semantics).
// Instant full text when usePrefersReducedMotion() is true. On each character
// change, optional per-char punctuation pauses give it a human cadence.
//
// Re-runs from the start whenever `text` changes. Cleans up its timer on unmount.
//
// ACCESSIBILITY. The animation is decoration, not content:
//   - The mutating span is `aria-hidden`. A live region that changes on every
//     character produces garbled, timing-dependent announcements, so assistive
//     tech reads a single static copy of the FULL text instead (visually
//     hidden, present from the first frame).
//   - The animation can be cut short. Typing at ~28ms/char gates a 140-char
//     transmission behind ~5 seconds, which is a long time to be unable to act.
//     Clicking the text completes it; so does bumping `skipSignal`, which lets
//     the consumer put a labelled control wherever its own chrome has a stable
//     spot for one (an inline button would slide around as the text grows).

import { useEffect, useRef, useState, useCallback } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

let caretStyleInjected = false;

function ensureCaretStyle(): void {
  if (caretStyleInjected || typeof document === 'undefined') return;
  const tag = document.createElement('style');
  tag.id = 'tw-typewriter-caret';
  tag.textContent = `
@keyframes twCaretBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.15; } }
.tw-caret { display: inline-block; width: 0.08em; min-width: 2px; height: 1em; vertical-align: text-bottom; margin-left: 2px; animation: twCaretBlink 1.05s ease-in-out infinite; }
.tw-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
@media (prefers-reduced-motion: reduce) { .tw-caret { animation: none; } }
`;
  document.head.appendChild(tag);
  caretStyleInjected = true;
}

export function Typewriter({
  text,
  speed = 28,
  onDone,
  className,
  style,
  caretColor,
  skipSignal = 0,
}: {
  text: string;
  /** base ms per character (non-punctuation) */
  speed?: number;
  onDone?: () => void;
  className?: string;
  style?: React.CSSProperties;
  /** caret colour; defaults to currentColor */
  caretColor?: string;
  /**
   * Bump to complete the animation immediately. Ignored at its initial value,
   * so a consumer that never touches it gets the plain typing behaviour.
   */
  skipSignal?: number;
}) {
  const reduceMotion = usePrefersReducedMotion();
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (reduceMotion || text.length === 0) {
      setTyped(text);
      setDone(true);
      onDoneRef.current?.();
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }

    setTyped('');
    setDone(false);
    let i = 0;
    const step = () => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) {
        setDone(true);
        onDoneRef.current?.();
        return;
      }
      const ch = text[i - 1];
      const delay = /[.,!?;:]/.test(ch) ? speed * 3 : speed * 0.5 + Math.random() * speed;
      timerRef.current = setTimeout(step, delay);
    };
    timerRef.current = setTimeout(step, speed * 4);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [text, speed, reduceMotion]);

  /** Jump straight to the finished state. Idempotent — onDone still fires once. */
  const skip = useCallback(() => {
    if (done) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setTyped(text);
    setDone(true);
    onDoneRef.current?.();
  }, [done, text]);

  // Parent-driven skip. Ignores the first render so a consumer that leaves
  // skipSignal alone never short-circuits the animation.
  const seenSignal = useRef(skipSignal);
  useEffect(() => {
    if (skipSignal === seenSignal.current) return;
    seenSignal.current = skipSignal;
    skip();
  }, [skipSignal, skip]);

  ensureCaretStyle();

  const canSkip = !done && !reduceMotion;

  return (
    <span className={className} style={style}>
      {/* The real text, in the a11y tree in full from the first frame and never
          re-announced. Assistive tech is never made to wait for the animation.
          The animated copy below is aria-hidden so the same words aren't read
          twice; its click handler is a pointer convenience, and the consumer's
          own skip control is the labelled, focusable route. */}
      <span className="tw-sr-only">{text}</span>

      <span
        aria-hidden
        style={{ cursor: canSkip ? 'pointer' : undefined }}
        onClick={canSkip ? skip : undefined}
      >
        {typed}
        {!done && !reduceMotion && (
          <span className="tw-caret" style={{ background: caretColor ?? 'currentColor' }} />
        )}
      </span>
    </span>
  );
}
